import api from '../config/api';
import { isApiEnabled } from '../config/appConfig';
import { getPersonNameError } from '../utils/personName';
import { getApiData, parseApiError } from './apiUtils';

const STORAGE_KEY = 'blf_register_draft';

export const getRegisterDraft = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
};

export const saveRegisterDraft = (data) => {
  const current = getRegisterDraft();
  const updated = { ...current, ...data };
  // No persistir credenciales en el borrador local.
  delete updated.password;
  delete updated.confirmPassword;
  delete updated.email;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};

export const clearRegisterDraft = () => {
  window.localStorage.removeItem(STORAGE_KEY);
};

const mapHijosPayload = (hijos) => {
  if (!Array.isArray(hijos)) {
    return [];
  }

  return hijos.map((child) => ({
    fecha_nacimiento: child.fechaNacimiento || child.fecha_nacimiento || '',
    genero: child.genero || '',
    intereses: Array.isArray(child.intereses) ? child.intereses : [],
  }));
};

const submitRegistrationLocal = (formData) => {
  const nombre = (formData.nombre || '').trim();
  const apellido = (formData.apellido || '').trim();
  const email = (formData.email || '').trim();
  const telefono = (formData.telefono || '').trim();
  const password = formData.password || '';
  const confirmPassword = formData.confirmPassword || '';

  if (!nombre || !apellido) {
    return Promise.reject(new Error('Nombre y apellido son obligatorios.'));
  }

  const nameError = getPersonNameError(nombre, apellido);
  if (nameError) {
    return Promise.reject(new Error(nameError));
  }

  if (!email) {
    return Promise.reject(new Error('El correo electrónico es obligatorio.'));
  }

  if (!telefono) {
    return Promise.reject(new Error('El número telefónico es obligatorio.'));
  }

  if (!formData.fechaNacimiento) {
    return Promise.reject(new Error('La fecha de nacimiento es obligatoria.'));
  }

  if (!formData.genero) {
    return Promise.reject(new Error('Selecciona un género.'));
  }

  if (!password) {
    return Promise.reject(new Error('La contraseña es obligatoria.'));
  }

  if (password !== confirmPassword) {
    return Promise.reject(new Error('Las contraseñas no coinciden.'));
  }

  if (!formData.aceptaTerminos) {
    return Promise.reject(new Error('Debes aceptar los términos y condiciones.'));
  }

  const registration = {
    id: Date.now(),
    nombre,
    apellido,
    email,
    telefono,
    fecha_nacimiento: formData.fechaNacimiento,
    genero: formData.genero,
    sin_hijos: Boolean(formData.sinHijos),
    hijos: formData.sinHijos ? [] : formData.hijos || [],
    acepta_terminos: Boolean(formData.aceptaTerminos),
    creado_en: new Date().toISOString(),
  };

  try {
    const raw = window.localStorage.getItem('blf_registrations');
    const stored = raw ? JSON.parse(raw) : [];
    const updated = Array.isArray(stored) ? [registration, ...stored] : [registration];
    window.localStorage.setItem('blf_registrations', JSON.stringify(updated));
    clearRegisterDraft();
  } catch (error) {
    return Promise.reject(new Error('No se pudo completar el registro.'));
  }

  return Promise.resolve(registration);
};

const validateRegistrationForm = (formData) => {
  const nombre = (formData.nombre || '').trim();
  const apellido = (formData.apellido || '').trim();
  const email = (formData.email || '').trim().toLowerCase();
  const telefono = (formData.telefono || '').trim();
  const password = formData.password || '';
  const confirmPassword = formData.confirmPassword || '';

  if (!nombre || !apellido) {
    throw new Error('Nombre y apellido son obligatorios.');
  }

  const nameError = getPersonNameError(nombre, apellido);
  if (nameError) {
    throw new Error(nameError);
  }

  if (!email) {
    throw new Error('El correo electrónico es obligatorio.');
  }

  if (!telefono) {
    throw new Error('El número telefónico es obligatorio.');
  }

  if (!formData.fechaNacimiento) {
    throw new Error('La fecha de nacimiento es obligatoria.');
  }

  if (!formData.genero) {
    throw new Error('Selecciona un género.');
  }

  if (!password) {
    throw new Error('La contraseña es obligatoria.');
  }

  if (password !== confirmPassword) {
    throw new Error('Las contraseñas no coinciden.');
  }

  if (!formData.aceptaTerminos) {
    throw new Error('Debes aceptar los términos y condiciones.');
  }

  return {
    nombre,
    apellido,
    email,
    telefono,
    fecha_nacimiento: formData.fechaNacimiento,
    genero: formData.genero,
    password,
    confirm_password: confirmPassword,
    sin_hijos: Boolean(formData.sinHijos),
    acepta_terminos: Boolean(formData.aceptaTerminos),
    hijos: formData.sinHijos ? [] : mapHijosPayload(formData.hijos),
  };
};

export const submitRegistration = (formData) => {
  if (!isApiEnabled()) {
    return submitRegistrationLocal(formData);
  }

  let payload;

  try {
    payload = validateRegistrationForm(formData);
  } catch (error) {
    return Promise.reject(error);
  }

  return api
    .post('/auth_registro.php', payload)
    .then((response) => {
      const data = getApiData(response, 'No se pudo completar el registro.');
      clearRegisterDraft();
      return data;
    })
    .catch((error) => {
      throw parseApiError(error);
    });
};
